import { PROMO_CODES, type PromoCode, toPromoCode } from './codes'
import { type PromoState, promo } from './store'

/**
 * Elements of the site's own that show only for some visitors' promo.
 *
 * Any element in Webflow can carry one attribute, or both, and then both have
 * to match:
 *
 * - `data-cv-promo-offer="FIRSTFREE"` shows it only to a visitor offered one
 *   of the codes it names, separated by spaces or commas, in any case.
 * - `data-cv-promo-applied="true"` shows it only once the visitor's promo is
 *   applied, and `"false"` only until then.
 *
 * The bundle hides an element that doesn't match with `data-cv-promo-hidden`,
 * which a stylesheet of its own turns into `display: none !important`, so it
 * wins over Webflow's classes and over an interaction's inline styles, and
 * marks `<html>` with `data-cv-promo-ready` once it has decided, for the
 * page's own CSS that hides these elements until then (README).
 */

export const OFFER_ATTR = 'data-cv-promo-offer'
export const APPLIED_ATTR = 'data-cv-promo-applied'
export const HIDDEN_ATTR = 'data-cv-promo-hidden'
export const READY_ATTR = 'data-cv-promo-ready'

const SELECTOR = `[${OFFER_ATTR}],[${APPLIED_ATTR}]`

type Visitor = Pick<PromoState, 'code' | 'active'>

/** Elements already warned about, so the page's observer doesn't repeat it. */
const warned = new WeakSet<Element>()

function warnOnce(element: Element, message: string): void {
  if (warned.has(element)) return
  warned.add(element)
  console.warn(`[codeverse] ${message}`, element)
}

/** The codes `data-cv-promo-offer` names. One that isn't ours warns, and matches nobody. */
function offeredCodes(element: Element, value: string): PromoCode[] {
  const names = value.split(/[\s,]+/).filter(Boolean)
  const codes = names.map(toPromoCode).filter((code) => code !== undefined)
  if (codes.length < names.length) {
    warnOnce(
      element,
      `${OFFER_ATTR}="${value}" names a code that isn't one of ${PROMO_CODES.join(', ')}`,
    )
  }
  return codes
}

/** `data-cv-promo-applied`'s answer: true, false, or undefined for a value that is neither. */
function wantsApplied(element: Element, value: string): boolean | undefined {
  const answer = value.trim().toLowerCase()
  // A bare attribute, as a hand-written one may be, means applied.
  if (answer === '' || answer === 'true' || answer === 'yes') return true
  if (answer === 'false' || answer === 'no') return false
  warnOnce(element, `${APPLIED_ATTR}="${value}" is neither "true" nor "false"`)
  return undefined
}

/** Whether `element` shows for `visitor`. An `applied` value that is neither shows either way. */
export function showsFor(element: Element, visitor: Visitor): boolean {
  const offer = element.getAttribute(OFFER_ATTR)
  if (offer !== null && !offeredCodes(element, offer).includes(visitor.code)) return false

  const applied = element.getAttribute(APPLIED_ATTR)
  if (applied === null) return true
  const wanted = wantsApplied(element, applied)
  return wanted === undefined || wanted === visitor.active
}

let styleInjected = false

/** The rule that hides what doesn't match, once per page, whichever copy of the bundle adds it. */
function ensureHiddenStyle(doc: Document): void {
  if (styleInjected) return
  styleInjected = true
  if (doc.querySelector('style[data-cv-promo]')) return
  const style = doc.createElement('style')
  style.setAttribute('data-cv-promo', '')
  style.textContent = `[${HIDDEN_ATTR}]{display:none!important}`
  doc.head.appendChild(style)
}

/**
 * Show or hide every marked element under `root` for `visitor`. An element
 * already right is left alone, so the page's MutationObserver, which calls
 * this for every change, settles at once.
 */
export function applyPromoVisibility(
  root: ParentNode = document,
  visitor: Visitor = promo.get(),
): void {
  ensureHiddenStyle(document)
  for (const element of root.querySelectorAll(SELECTOR)) {
    const hide = !showsFor(element, visitor)
    if (hide !== element.hasAttribute(HIDDEN_ATTR)) element.toggleAttribute(HIDDEN_ATTR, hide)
  }
  document.documentElement.setAttribute(READY_ATTR, '')
}

/**
 * Show the page's marked elements for the visitor's promo now, and again
 * whenever it changes: a sign-up, one through the other code's form, or one
 * in another tab.
 */
export function watchPromoVisibility(): () => void {
  applyPromoVisibility()
  return promo.subscribe((state, previous) => {
    if (state.code !== previous.code || state.active !== previous.active) {
      applyPromoVisibility(document, state)
    }
  })
}

/** Test helper: the next call adds the stylesheet again. */
export function resetPromoVisibility(): void {
  styleInjected = false
}
